import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { apps } from "../data/apps";
import { isLiveOnPlay, ratingLabel, installsLabel, PORTFOLIO } from "../data/appStats";
import "../App.css";

const liveApps = apps.filter((a: any) => isLiveOnPlay(a.id));
const categories = ["All", ...Array.from(new Set(liveApps.map((a: any) => a.category as string)))];

const playClick = (e: React.MouseEvent, url: string, appId: string) => {
  e.preventDefault();
  e.stopPropagation();
  try {
    const w = window as any;
    w.gtag?.("event", "app_cta_click", { app_id: appId, source: "apps_index" });
    if (w.gtag_report_conversion) { w.gtag_report_conversion(url); return; }
  } catch {}
  window.open(url, "_blank", "noopener");
};

const PlayGlyph: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.2l2.807 1.626a1 1 0 010 1.734l-2.808 1.626L15.206 12l2.492-2.493zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" />
  </svg>
);

const AppsIndex: React.FC = () => {
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");

  const shown = liveApps.filter((a: any) => {
    if (cat !== "All" && a.category !== cat) return false;
    if (q && !`${a.name} ${a.tagline} ${a.description} ${a.category}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const itemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Cosmovex Android Apps",
    numberOfItems: liveApps.length,
    itemListElement: liveApps.map((a: any, i: number) => {
      const r = ratingLabel(a.id);
      return {
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "MobileApplication",
          name: a.name,
          description: a.description,
          operatingSystem: "Android",
          applicationCategory: "DeveloperApplication",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          url: `https://cosmovexapps.com/apps/${a.id}`,
          installUrl: a.playstore,
          ...(r && { aggregateRating: { "@type": "AggregateRating", ratingValue: r.rating, ratingCount: r.count } }),
        },
      };
    }),
  };

  return (
    <div className="cva">
      <style>{cva_css}</style>
      <Helmet>
        <title>{`All Apps — ${liveApps.length} Free Android Apps for Developers | Cosmovex`}</title>
        <meta
          name="description"
          content={`Browse all ${liveApps.length} free Cosmovex Android apps: code editors, Python & Java compilers, SSH terminal, JSON/SQL/Markdown/CSV editors, PDF tools and more. ${PORTFOLIO.downloads} downloads. Free, offline-first, no account.`}
        />
        <link rel="canonical" href="https://cosmovexapps.com/apps" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cosmovexapps.com/apps" />
        <meta property="og:title" content={`All Apps — ${liveApps.length} Free Android Apps for Developers | Cosmovex`} />
        <meta property="og:description" content="Code editors, compilers, SSH, JSON, Markdown, PDF — every Cosmovex app is free, offline-first and account-free." />
        <meta property="og:site_name" content="Cosmovex" />
        <meta name="twitter:card" content="summary" />
        <script type="application/ld+json">{JSON.stringify(itemList)}</script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://cosmovexapps.com/" },
              { "@type": "ListItem", position: 2, name: "Apps", item: "https://cosmovexapps.com/apps" },
            ],
          })}
        </script>
      </Helmet>

      <div className="cva-glow" aria-hidden="true" />

      <header className="cva-hdr">
        <div className="cva-wrap cva-hdr-in">
          <Link to="/" className="cva-brand"><img src="/logo192.png" alt="" className="cva-logo" width="30" height="30" /> Cosmovex</Link>
          <nav className="cva-nav">
            <Link to="/apps" className="on">Apps</Link>
            <Link to="/articles">Guides</Link>
            <Link to="/about">About</Link>
          </nav>
        </div>
      </header>

      <main className="cva-wrap cva-main">
        <div className="cva-prompt" aria-hidden="true">
          <span className="cva-prompt-ps">~/cosmovex</span> $ ls apps/ <span className="cva-prompt-out"># {liveApps.length} found</span>
          <span className="cva-caret" />
        </div>
        <h1 className="cva-h1">Every app we <span className="cva-grad">ship</span>.</h1>
        <p className="cva-sub">
          Focused Android apps for people who build things. Each one does a single job well —
          free, offline-first, zero accounts.
        </p>

        <div className="cva-statbar">
          <span className="cva-statchip"><strong>{liveApps.length}</strong> apps</span>
          <span className="cva-statchip"><strong>{PORTFOLIO.downloads}</strong> installs</span>
          <span className="cva-statchip"><span className="cva-star">★</span> <strong>{PORTFOLIO.avgRating}</strong> avg</span>
        </div>

        <div className="cva-filters">
          <div className="cva-chips" role="tablist" aria-label="Filter by category">
            {categories.map((c) => (
              <button key={c} className={`cva-chip${cat === c ? " on" : ""}`} onClick={() => setCat(c)}>{c}</button>
            ))}
          </div>
          <input
            className="cva-search"
            type="search"
            placeholder="/ search apps…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search apps"
          />
        </div>

        <div className="cva-grid">
          {shown.map((a: any) => {
            const r = ratingLabel(a.id);
            const inst = installsLabel(a.id);
            return (
              <div key={a.id} className="cva-card" style={{ ["--ac" as any]: "#A8720E" }}>
                <Link to={`/apps/${a.id}`} className="cva-card-main">
                  <div className="cva-card-top">
                    <span className="cva-ic-halo"><img src={a.icon} alt={`${a.name} icon`} loading="lazy" className="cva-ic" /></span>
                    <div className="cva-card-id">
                      <div className="cva-name">{a.name}</div>
                      <div className="cva-cat">{`// ${a.category.toLowerCase()}`}</div>
                    </div>
                  </div>
                  <p className="cva-tag">{a.tagline}</p>
                  <div className="cva-stats">
                    {r && <span className="cva-stars"><span className="cva-star">★</span> {r.rating.toFixed(1)}</span>}
                    {inst && <span className="cva-inst">{inst.replace(" downloads", " installs")}</span>}
                    {!r && !inst && <span className="cva-inst">free · no account</span>}
                  </div>
                </Link>
                <div className="cva-foot">
                  <span className="cva-btns">
                    <a
                      href={a.playstore}
                      onClick={(e) => playClick(e, a.playstore, a.id)}
                      className="cva-install"
                      rel="noopener noreferrer"
                      aria-label={`Install ${a.name} from Google Play`}
                    >
                      <PlayGlyph /> Install free
                    </a>
                    {a.appstore && (
                      <a
                        href={a.appstore}
                        onClick={(e) => playClick(e, a.appstore, a.id)}
                        className="cva-ios"
                        rel="noopener noreferrer"
                        aria-label={`Download ${a.name} on the App Store`}
                        title="Also on iOS — App Store"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                        </svg>
                        iOS
                      </a>
                    )}
                  </span>
                  <Link to={`/apps/${a.id}`} className="cva-more">Details <span className="cva-more-arrow">→</span></Link>
                </div>
              </div>
            );
          })}
        </div>
        {shown.length === 0 && (
          <div className="cva-none">
            <span className="cva-none-code">404</span> — no apps match <strong>"{q}"</strong>. Try another search.
          </div>
        )}
      </main>

      <footer className="cva-footer">
        <div className="cva-wrap">
          <Link to="/">Home</Link> · <Link to="/articles">Guides</Link> · <Link to="/privacy-policy">Privacy</Link> · <Link to="/terms-of-service">Terms</Link>
          <p>© {new Date().getFullYear()} Cosmovex. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

const cva_css = `
.cva{background:#FBFAF6;color:#26313B;font-family:"Inter",-apple-system,BlinkMacSystemFont,sans-serif;-webkit-font-smoothing:antialiased;min-height:100vh;position:relative;overflow-x:hidden}
.cva a{text-decoration:none;color:inherit}
.cva-wrap{max-width:1180px;margin:0 auto;padding:0 24px}
.cva-star{color:#A8720E}
.cva-grad{color:#A8720E}
.cva-glow{display:none}

.cva-hdr{position:sticky;top:0;z-index:40;background:rgba(251,250,246,.88);backdrop-filter:blur(12px);border-bottom:1px solid #E1DCCE}
.cva-hdr-in{display:flex;align-items:center;justify-content:space-between;height:64px}
.cva-brand{display:flex;align-items:center;gap:10px;font-weight:700;font-size:18px;font-family:"Space Grotesk",sans-serif;color:#26313B}
.cva-logo{width:30px;height:30px;border-radius:8px;object-fit:cover}
.cva-nav{display:flex;gap:24px;font-size:14px;color:#5B6B7C;font-weight:500}
.cva-nav a:hover{color:#26313B}
.cva-nav a.on{color:#26313B;font-weight:600}

.cva-main{padding:52px 24px 70px;position:relative}
.cva-prompt{font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;font-size:13.5px;color:#3D4B59;background:#F1EEE5;border:1px solid #E1DCCE;border-radius:10px;display:inline-flex;align-items:center;gap:7px;padding:9px 15px;margin-bottom:26px}
.cva-prompt-ps{color:#0F6BBF}
.cva-prompt-out{color:#8A93A2}
.cva-caret{width:8px;height:15px;background:#A8720E;display:inline-block;animation:cvablink 1.1s steps(1) infinite}
@keyframes cvablink{50%{opacity:0}}
@media(prefers-reduced-motion:reduce){.cva-caret{animation:none}}
.cva-h1{font-family:"Space Grotesk",sans-serif;font-weight:700;font-size:clamp(36px,5.5vw,56px);letter-spacing:-.03em;margin:0 0 14px;line-height:1.04;color:#26313B}
.cva-sub{color:#3D4B59;font-size:16.5px;line-height:1.65;max-width:560px;margin:0 0 26px}
.cva-statbar{display:flex;gap:10px;flex-wrap:wrap;margin-bottom:34px}
.cva-statchip{font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;font-size:12.5px;color:#5B6B7C;border:1px solid #E1DCCE;background:#FFFFFF;border-radius:999px;padding:7px 14px}
.cva-statchip strong{color:#26313B;font-weight:700}

.cva-filters{display:flex;align-items:center;justify-content:space-between;gap:14px;flex-wrap:wrap;margin-bottom:26px}
.cva-chips{display:flex;gap:8px;flex-wrap:wrap}
.cva-chip{background:#FFFFFF;border:1px solid #E1DCCE;color:#5B6B7C;font-family:inherit;font-size:13px;font-weight:600;border-radius:999px;padding:8px 15px;cursor:pointer;transition:.14s}
.cva-chip:hover{color:#26313B;border-color:#D9A32E;transform:translateY(-1px)}
.cva-chip.on{background:#E8B44C;border-color:#D9A32E;color:#221A06;box-shadow:0 1px 2px rgba(52,44,26,.06),0 4px 14px rgba(52,44,26,.05)}
.cva-search{background:#FFFFFF;border:1px solid #E1DCCE;border-radius:11px;color:#26313B;padding:10px 15px;font-size:13.5px;font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;min-width:230px;transition:.15s}
.cva-search:focus{outline:none;border-color:#A8720E;box-shadow:0 0 0 3px rgba(168,114,14,.18)}
.cva-search::placeholder{color:#8A93A2}

.cva-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(310px,1fr));gap:16px}
.cva-card{border:1px solid #E1DCCE;background:#FFFFFF;border-radius:18px;padding:20px;transition:transform .18s,border-color .18s,box-shadow .18s;position:relative;overflow:hidden;display:flex;flex-direction:column;box-shadow:0 1px 2px rgba(52,44,26,.06),0 4px 14px rgba(52,44,26,.05)}
.cva-card::before{content:"";position:absolute;inset:0 0 auto;height:2px;background:var(--ac);opacity:.55}
.cva-card:hover{border-color:#D9A32E;transform:translateY(-3px);box-shadow:0 18px 44px rgba(52,44,26,.12)}
.cva-card-main{flex:1;display:block}
.cva-card-top{display:flex;align-items:center;gap:14px;margin-bottom:13px}
.cva-ic-halo{position:relative;flex:none;display:grid;place-items:center}
.cva-ic-halo::before{content:"";position:absolute;inset:-6px;border-radius:18px;background:#F1EEE5;filter:blur(8px);opacity:.8}
.cva-ic{width:54px;height:54px;border-radius:14px;border:1px solid #E1DCCE;object-fit:cover;position:relative}
.cva-card-id{min-width:0}
.cva-name{font-family:"Space Grotesk",sans-serif;font-weight:600;font-size:17px;margin-bottom:3px;letter-spacing:-.01em;color:#26313B}
.cva-cat{font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;font-size:11px;color:#A8720E;letter-spacing:.02em}
.cva-tag{font-size:13.5px;color:#5B6B7C;line-height:1.55;margin:0 0 12px}
.cva-stats{display:flex;align-items:center;gap:12px;font-size:12.5px;margin-bottom:16px;font-family:"JetBrains Mono",ui-monospace,Menlo,monospace}
.cva-stars{font-weight:700;color:#26313B}
.cva-inst{color:#8A93A2}
.cva-foot{display:flex;align-items:center;justify-content:space-between;gap:10px}
.cva-btns{display:inline-flex;gap:9px;align-items:center}
.cva .cva-ios{display:inline-flex;align-items:center;gap:6px;border:1px solid #E1DCCE;color:#3D4B59;font-weight:700;font-size:12.5px;border-radius:11px;padding:9px 13px;transition:.15s;background:#FFFFFF}
.cva .cva-ios:hover{border-color:#D9A32E;color:#26313B;transform:translateY(-1px)}
.cva .cva-install{display:inline-flex;align-items:center;gap:8px;background:#E8B44C;border:1px solid #D9A32E;color:#221A06;font-weight:700;font-size:13.5px;letter-spacing:.01em;border-radius:11px;padding:10px 18px;transition:transform .15s,box-shadow .15s,filter .15s;box-shadow:0 1px 2px rgba(52,44,26,.06),0 4px 14px rgba(52,44,26,.05)}
.cva .cva-install:hover{transform:translateY(-2px);filter:brightness(1.04);box-shadow:0 8px 26px rgba(52,44,26,.16)}
.cva .cva-install:active{transform:translateY(0)}
.cva-more{font-size:13px;color:#5B6B7C;font-weight:600;display:inline-flex;align-items:center;gap:5px}
.cva-more-arrow{transition:transform .15s;display:inline-block}
.cva-more:hover{color:#26313B}
.cva-more:hover .cva-more-arrow{transform:translateX(3px)}

.cva-none{color:#5B6B7C;padding:44px 0;font-size:15px}
.cva-none-code{font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;color:#A8720E;font-weight:700}
.cva-none strong{color:#26313B}

.cva-footer{border-top:1px solid #E1DCCE;padding:28px 0 36px;color:#5B6B7C;font-size:13.5px}
.cva-footer a:hover{color:#26313B}
.cva-footer p{color:#8A93A2;font-size:12.5px;margin-top:10px}
`;

export default AppsIndex;
